/**
 * 환경에 따른 조건부 로깅 유틸리티
 * - 프로덕션 환경에서는 debug/log 레벨 출력 비활성화
 * - warn/error는 항상 출력
 */

const isProduction = process.env.NODE_ENV === 'production'

type LogLevel = 'debug' | 'log' | 'info' | 'warn' | 'error'

interface Logger {
  debug: (...args: unknown[]) => void
  log: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

/**
 * 프로덕션 환경에서는 debug/log 레벨을 숨기는 로거
 *
 * @example
 * ```ts
 * import { logger } from '@/utils/logger'
 *
 * logger.debug('[Component] Debug message') // 프로덕션에서 숨김
 * logger.log('[Component] Log message')     // 프로덕션에서 숨김
 * logger.info('[Component] Info message')   // 프로덕션에서 숨김
 * logger.warn('[Component] Warning')        // 항상 출력
 * logger.error('[Component] Error')         // 항상 출력
 * ```
 */
export const logger: Logger = {
  debug: (...args: unknown[]) => {
    if (!isProduction) {
      console.debug(...args)
    }
  },

  log: (...args: unknown[]) => {
    if (!isProduction) {
      console.log(...args)
    }
  },

  info: (...args: unknown[]) => {
    if (!isProduction) {
      console.info(...args)
    }
  },

  warn: (...args: unknown[]) => {
    console.warn(...args)
  },

  error: (...args: unknown[]) => {
    console.error(...args)
  },
}

/**
 * 태그가 포함된 로거 생성
 *
 * @example
 * ```ts
 * const authLogger = createTaggedLogger('Auth')
 * authLogger.debug('Setting tokens') // [Auth] Setting tokens
 * ```
 */
export function createTaggedLogger(tag: string): Logger {
  const prefix = `[${tag}]`

  return {
    debug: (...args: unknown[]) => logger.debug(prefix, ...args),
    log: (...args: unknown[]) => logger.log(prefix, ...args),
    info: (...args: unknown[]) => logger.info(prefix, ...args),
    warn: (...args: unknown[]) => logger.warn(prefix, ...args),
    error: (...args: unknown[]) => logger.error(prefix, ...args),
  }
}

export default logger
